import React from 'react';
import { Link } from 'react-router-dom';
import { Crown, X, Clock, CheckCircle2, Ticket } from 'lucide-react';

const pill = (txt, tone) => {
  const tones = {
    green: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    gray: 'bg-[#F4F4F5] text-[#52525B] border-[#E4E4E7]',
  };
  return <span className={`px-2 py-0.5 rounded border text-[10px] font-bold uppercase tracking-[0.06em] ${tones[tone]}`}>{txt}</span>;
};

export const paymentPill = (s) => {
  if (s === 'paid') return pill('Paid', 'green');
  if (s === 'free') return pill('Free', 'gray');
  if (s === 'team_pending') return pill('Leader pays', 'amber');
  return pill('Payment due', 'amber');
};

export const TeamMemberList = ({ team, eventId, myUserId, isLeader, onRemove, onRevoke }) => (
  <div className="space-y-2" data-testid="team-member-list">
    {team.members.map((m) => (
      <div key={m.id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-3 p-3 rounded-lg border border-[#E4E4E7] bg-white" data-testid={`team-member-${m.id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full bg-[#0A0A0A] text-white flex items-center justify-center text-sm font-bold flex-shrink-0">{(m.name || '?')[0]}</div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-[#0A0A0A] truncate flex items-center gap-1.5">
              {m.name}{m.role === 'leader' && <Crown size={13} className="text-[#F59E0B]" />}
              {m.user_id === myUserId && <span className="text-[10px] text-[#A1A1AA] font-medium">(you)</span>}
              {m.account_status === 'unclaimed' && <span className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 border border-violet-200 text-[9px] font-bold uppercase tracking-[0.06em]" data-testid={`member-provisional-${m.id}`}>Provisional · awaiting login</span>}
            </p>
            <p className="text-xs text-[#A1A1AA] truncate">{m.email}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:flex-shrink-0 pl-12 sm:pl-0">
          {paymentPill(m.payment_status)}
          {m.ticket_status === 'active' ? pill('Ticket active', 'green') : pill('Ticket pending', 'gray')}
          {m.ticket_status === 'active' && m.user_id === myUserId && m.registration_id && (
            <Link to={`/events/${eventId}/ticket/${m.registration_id}`} className="text-[#002FA7]" title="View ticket" data-testid="my-team-ticket-link"><Ticket size={16} /></Link>
          )}
          {isLeader && team.status === 'forming' && m.role !== 'leader' && (
            <button onClick={() => onRemove(m)} className="text-[#A1A1AA] hover:text-red-600" title="Remove" data-testid={`remove-member-${m.id}`}><X size={16} /></button>
          )}
        </div>
      </div>
    ))}
    {(team.pending_invites || []).map((inv) => (
      <div key={inv.id} className="flex items-center justify-between gap-3 p-3 rounded-lg border border-dashed border-[#D4D4D8] bg-[#FAFAFA]" data-testid={`pending-invite-${inv.id}`}>
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-9 h-9 rounded-full border border-dashed border-[#A1A1AA] flex items-center justify-center"><Clock size={14} className="text-[#A1A1AA]" /></div>
          <div className="min-w-0">
            <p className="text-sm font-medium text-[#52525B] truncate">{inv.email}</p>
            <p className="text-xs text-[#A1A1AA]">Invited · expires {new Date(inv.expires_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</p>
          </div>
        </div>
        {isLeader && (
          <button onClick={() => onRevoke(inv)} className="text-xs font-semibold text-red-500 hover:text-red-600" data-testid={`revoke-invite-${inv.id}`}>Revoke</button>
        )}
      </div>
    ))}
  </div>
);

export const TeamStatusBanner = ({ team, cfg }) => {
  const map = {
    forming: ['Forming', `Build a team of ${cfg.min_size}–${cfg.max_size}. Lock it when everyone has joined.`, 'amber'],
    locked: ['Locked', 'Membership is final. Complete payment to confirm the team.', 'amber'],
    confirmed: ['Confirmed', 'Your team is registered. Every member has an individual ticket.', 'green'],
    cancelled: ['Cancelled', 'This team was cancelled.', 'gray'],
  };
  const [label, text, tone] = map[team.status] || map.forming;
  return (
    <div className={`flex items-start gap-3 p-3.5 rounded-lg border ${tone === 'green' ? 'bg-emerald-50 border-emerald-200' : tone === 'amber' ? 'bg-amber-50 border-amber-200' : 'bg-[#F4F4F5] border-[#E4E4E7]'}`} data-testid="team-status-banner">
      <CheckCircle2 size={18} className={tone === 'green' ? 'text-emerald-600 mt-0.5' : 'text-amber-600 mt-0.5'} />
      <div>
        <p className="text-sm font-bold text-[#0A0A0A]" data-testid="team-status-label">{label}</p>
        <p className="text-xs text-[#52525B] mt-0.5">{text}</p>
      </div>
    </div>
  );
};
